import { FIN_CATEGORIES, type FinCategory, isAsset } from '#dictionary'

import { FinancialRecord } from './financialRecords'
import type { FinancialRecordSummary } from './types'

export const DRAWDOWN_AMOUNT_STEP = 100

export const roundUpToStep = (value: number): number => {
  const result = Math.ceil(value / DRAWDOWN_AMOUNT_STEP) * DRAWDOWN_AMOUNT_STEP
  return Object.is(result, -0) ? 0 : result
}

type DrawDownResult = {
  recipients: FinancialRecordSummary[]
  totalValue: number
  satisfied: boolean
}

class AssetDrawDownService {
  private readonly recordById: Map<string, FinancialRecord>

  constructor(records: readonly FinancialRecord[]) {
    this.recordById = new Map(records.map(r => [r.id, r]))
  }

  // TODO: #must drawdown should take into account asset's opening balance
  // means the end balance of previous year, not current one
  getRecipients(
    valueToDrawdown: number,
    assetsBreakdown: FinancialRecordSummary[]
  ): DrawDownResult {
    const totalValueToDrawdown =
      roundUpToStep(valueToDrawdown) + DRAWDOWN_AMOUNT_STEP

    const recipients: FinancialRecordSummary[] = []
    let remainingToDrawdown = totalValueToDrawdown

    for (const drawdownCategory of this.drawdownPriority) {
      if (remainingToDrawdown <= 0) break

      const categoryAssets = assetsBreakdown.filter(asset => {
        const record = this.recordById.get(asset.id)
        return record?.finCategory.key === drawdownCategory.key
      })

      const positiveAssets = categoryAssets.filter(asset => asset.value > 0)
      if (positiveAssets.length === 0) continue

      for (const asset of positiveAssets) {
        if (remainingToDrawdown <= 0) break

        const withdrawn = this.clampedWithdrawalAmount(
          asset,
          remainingToDrawdown
        )

        recipients.push({
          id: asset.id,
          name: asset.name,
          value: withdrawn,
          isLiquid: asset.isLiquid,
        })
        remainingToDrawdown -= withdrawn
      }
    }

    const totalValue = recipients.reduce((sum, { value }) => sum + value, 0)

    const satisfied = totalValue >= valueToDrawdown

    return { recipients, totalValue, satisfied }
  }

  private clampedWithdrawalAmount(
    asset: FinancialRecordSummary,
    remaining: number
  ): number {
    const maxAvailable = Math.min(remaining, asset.value)

    const amount =
      maxAvailable === asset.value ? maxAvailable : roundUpToStep(maxAvailable)

    return Math.min(amount, asset.value)
  }

  private get drawdownPriority(): FinCategory[] {
    const drawDownCategories = FIN_CATEGORIES.filter(
      category =>
        isAsset(category) &&
        typeof category.a_drawDownOrder === 'number' &&
        category.a_withdrawalCategoryKey
    )

    return [...drawDownCategories].sort(
      (a, b) => Number(a.a_drawDownOrder) - Number(b.a_drawDownOrder)
    )
  }
}

export default AssetDrawDownService
